import type { FC } from "react";
import React from "react";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import { AppQueryPopupsController } from "../AppQueryPopupsBloc";
import translate from "src/lib/translate";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import Translate from "components/Translate/Translate";
import { Button } from "@9amhealth/shared";
import UserJourneyBloc, {
  JourneyType
} from "../../UserJourneyOverview/UserJourneyBloc";
import { useBloc } from "@blac/react";
import JOURNEY_CONFIG from "../../UserJourneyOverview/journeyConfig";
import { tracker } from "src/state/state";

const List = styled.ol`
  padding-left: 1.25rem;
  margin: 0;
  color: var(--color-charcoal-80);

  li {
    margin: 0.5rem 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: var(--journey-dialog-bg-gradient);
  background-size: var(--journey-dialog-bg-size);
  background-position: var(--journey-dialog-bg-position);
  background-repeat: no-repeat;
  z-index: -2;
`;

const Eclipse = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  overflow: hidden;
  z-index: -1;

  &::after {
    content: "";
    position: absolute;
    bottom: -100%;
    left: -100%;
    right: -100%;
    height: 200%;
    border-radius: 50%;
    background-image: var(--journey-dialog-bg-eclipse);
    background-size: var(--journey-dialog-eclipse-size);
    background-position: var(--journey-dialog-eclipse-position);
    background-repeat: no-repeat;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.5rem;
  padding: 1.25rem;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 10rem 1.25rem;
  }
`;

const Duration = styled.small`
  color: var(--color-charcoal-80);
`;

const Description = styled.span`
  color: var(--color-charcoal-80);
  font-weight: 500;
`;

const ChooseYourNextJourneyDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [searchParams] = useSearchParams();

  const [, { enrollInJourney }] = useBloc(UserJourneyBloc);
  const colorScheme = searchParams.get("colorScheme") ?? "";
  const journey = searchParams.get("journey") ?? "";
  const popup = searchParams.get("popup") ?? "";

  const journeyConfig = JOURNEY_CONFIG[journey as keyof typeof JOURNEY_CONFIG];

  const onClose = () => {
    tracker.track("App Popup Closed", {
      data: {
        "Popup ID": popup
      }
    });
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  return (
    <InDialog
      popup
      title={translate("journey.chooseYourJourney.popup.title")}
      onClose={onClose}
      returnUrl={returnUrl}
    >
      <Wrapper data-color-scheme={colorScheme}>
        <Background />

        <Content>
          <div>
            <h2>
              <Translate
                msg="journey.title"
                variables={{
                  context: journey
                }}
              />
            </h2>

            <Duration>
              <Translate msg="journey.chooseYourJourney.duration" />
            </Duration>
          </div>

          <Description>
            <Translate
              msg="journey.chooseYourJourney.journeyDescription"
              variables={{
                context: journey
              }}
            />
          </Description>

          <List>
            {journeyConfig.lessons.map((c) => (
              <li key={c}>
                <Translate
                  msg={`journey.chooseYourJourney.${journeyConfig.translationKey}.listItem`}
                  variables={{ context: c }}
                />
              </li>
            ))}
          </List>
        </Content>
        <Button
          theme="charcoal"
          fullWidth
          onPress={() => {
            void enrollInJourney(journey as JourneyType, "journey-modal");
            AppQueryPopupsController.closePopup();
          }}
        >
          <Translate msg="journey.chooseYourJourney.buttonSelect" />
        </Button>
        <Eclipse />
      </Wrapper>
    </InDialog>
  );
};

export default ChooseYourNextJourneyDialog;
